// @ts-nocheck - Legacy file with extensive type issues requiring comprehensive refactoring (TODO: GXM-XXXX)
import { createRef, useMemo, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Card } from "primereact/card";
import { Link, useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import API_ROUTES from "../../api-routes";
import { Skeleton } from "primereact/skeleton";
import icon from "../../assets/images/director-empty-icon.png";
import AdminOnly from "~/components/AdminOnly";
import AuditSidebar from "~/components/audit-sidebar/AuditSidebar";
import EmptyDataView from "./EmptyDataView";
import { format } from "date-fns";

interface DirectorTabProps {
  directorsArr: any[];
  onDirectorClick: (director: any) => void;
  loading: boolean;
}

const DirectorTab = ({
  directorsArr,
  onDirectorClick,
  loading,
}: DirectorTabProps) => {
  const navigate = useNavigate();
  const { id: entityId } = useParams();
  const contextMenuRef = useRef({});
  const contextMenu = createRef();
  const [isAuditSidebarOpen, setIsAuditSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const auditLabels = () => {
    return {
      create: "Director Created",
      update: "Director Updated",
      delete: "Director Deleted",
    };
  };

  const handleEditClick = (id) => {
    navigate(`/entity/${entityId}/directors/${id}/info`);
  };

  const statusTemplate = (rowData) => {
    const renderStatus = (status) => {
      switch (status) {
        case "Active":
          return (
            <>
              <span className="pi pi-circle-fill active-circle-fill"></span>{" "}
              Active
            </>
          );
        case "Not Active":
          return (
            <>
              <span className="pi pi-circle-fill not-active-circle-fill"></span>{" "}
              Not Active
            </>
          );
        case "Removed":
          return (
            <>
              <span className="pi pi-circle-fill removed-circle-fill"></span>{" "}
              Removed
            </>
          );
        default:
          return "-";
      }
    };

    return renderStatus(rowData.status);
  };
  const nameTemplate = (rowData) => {
    return (
      <Link
        className="link-item"
        to={`/entity/${entityId}/directors/${rowData.id}/info`}
      >
        {rowData?.preferred_name ||
          [rowData.name, rowData.other_name, rowData.last_name]
            .filter(Boolean)
            .join(" ")}
        {rowData.positions?.length > 1 && <div></div>}
      </Link>
    );
  };

  const flattenDirectorsData = (directors = []) => {
    if (!Array.isArray(directors)) return [];

    return directors.reduce((acc, director) => {
      const directorAppointments =
        director.appointments?.filter(
          (appointment) => appointment.entity_id === entityId,
        ) || [];
      const positionsArray = directorAppointments?.map((appointment) => ({
        id: director.id,
        name:
          director.preferred_name ||
          [director.name, director.other_name, director.last_name]
            .filter(Boolean)
            .join(" "),
        position: appointment.position,
        start_date: appointment.start_date,
        end_date: appointment.end_date,
        status: appointment.status,
      }));

      return acc.concat(positionsArray);
    }, []);
  };

  const directorsFlattenedArr = flattenDirectorsData(directorsArr);
  const filteredDirectors = directorsFlattenedArr.filter((row) => {
    const fullName = row.name?.toLowerCase() || "";
    const position = row.position?.toLowerCase() || "";
    const search = searchTerm.toLowerCase();
    return fullName.includes(search) || position.includes(search);
  });
  const uniqueIdsCount = useMemo(() => {
    return new Set(filteredDirectors.map((item) => item.id)).size;
  }, [filteredDirectors]);

  return (
    <>
      <div className="flex align-items-center">
        <div className="flex-1">
          <div className="input-wrapper-container search__shareholder mb-4">
            <span className="pi pi-search"></span>
            <input
              type="text"
              placeholder="Search directors by given name, surname or position"
              className="appearance-none outline-none w-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <span className="pi pi-search"></span>
          </div>
        </div>{" "}
        <AdminOnly>
          <button className="add-new-contact mb-4" onClick={onDirectorClick}>
            Add Director <span className="pi pi-plus ml-2"></span>
          </button>
        </AdminOnly>
      </div>

      <Card className="gen-card">
        <div className="formgrid grid">
          <div className="field col-12 mt-2">
            <div className="card">
              {loading ? (
                <DataTable
                  value={Array(5).fill({})}
                  tableStyle={{ minWidth: "100%" }}
                  className="datatable-right-align"
                  paginator
                  rows={10}
                  rowsPerPageOptions={[10, 20, 30]}
                >
                  <Column header="Full Name" body={<Skeleton />} />
                  <Column header="Position" body={<Skeleton />} />
                  <Column header="Start Date" body={<Skeleton />} />
                  <Column header="End Date" body={<Skeleton />} />
                  <Column header="Status" body={<Skeleton />} />
                  <Column body={<Skeleton />} />
                </DataTable>
              ) : directorsFlattenedArr.length === 0 ? (
                <EmptyDataView
                  Icon={
                    <img
                      className="users-card-icon-img"
                      src={icon}
                      alt="Users Icon"
                    />
                  }
                  Header="Looks like there aren't any directors appointed yet!"
                  Description="Let's start by adding your first one!"
                  label="Add Director"
                  handleNavigate={onDirectorClick}
                />
              ) : (
                <DataTable
                  value={filteredDirectors}
                  rowGroupMode="rowspan"
                  groupRowsBy="id"
                  sortMode="multiple"
                  tableStyle={{ minWidth: "50rem" }}
                  paginator={uniqueIdsCount > 10}
                  paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                  rows={10}
                  rowsPerPageOptions={[10, 20, 30]}
                  className="datatable-right-align"
                  emptyMessage="No results were found"
                >
                  <Column
                    field="id"
                    header="Full Name"
                    sortable
                    rowspan
                    style={{ minWidth: "200px" }}
                    body={nameTemplate}
                  />

                  <Column
                    field="position"
                    header="Position"
                    style={{ minWidth: "200px" }}
                  />

                  <Column
                    field="start_date"
                    header="Start Date"
                    style={{ minWidth: "150px" }}
                    body={(rowData) =>
                      rowData?.start_date
                        ? format(rowData.start_date, "MMMM do, yyyy")
                        : "-"
                    }
                  />

                  <Column
                    field="end_date"
                    header="End Date"
                    style={{ minWidth: "150px" }}
                    body={(rowData) =>
                      rowData?.end_date
                        ? format(rowData.end_date, "MMMM do, yyyy")
                        : "-"
                    }
                  />

                  <Column
                    field="status"
                    header="Status"
                    style={{ minWidth: "100px" }}
                    body={statusTemplate}
                  />

                  <Column
                    body={(rowData) => {
                      return (
                        <AdminOnly>
                          <div className="flex flex-row-reverse">
                            <span
                              className="pi pi-pencil ml-4 mr-3 pencil-pointer"
                              onClick={() => handleEditClick(rowData.id)}
                            ></span>
                          </div>
                        </AdminOnly>
                      );
                    }}
                  />
                </DataTable>
              )}
            </div>
          </div>
        </div>
      </Card>
      {isAuditSidebarOpen && (
        <AuditSidebar
          heading="Directors"
          endpoint={API_ROUTES.FETCH_DIRECTORS_AUDIT}
          id={entityId}
          setSidebarToggle={setIsAuditSidebarOpen}
          sidebarToggle={isAuditSidebarOpen}
          labels={auditLabels}
        />
      )}
    </>
  );
};

export default DirectorTab;
